import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  Input,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

// Converts [2026, 3, 16] → dayjs object for DatePicker
const parseDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return null;
  const [y, m, d] = arr;
  return dayjs(
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
  );
};

const CURRENCY_OPTIONS = [
  { label: "NGN – Nigerian Naira", value: "NGN" },
  { label: "USD – US Dollar", value: "USD" },
  { label: "EUR – Euro", value: "EUR" },
  { label: "GBP – British Pound", value: "GBP" },
];

// ── Disable only the action that matches the current status ───────────────
const STATUS_TO_DISABLED_ACTION = {
  Draft: "open_auction",
  Open: "open_auction",
  Closed: "close_auction",
  Published: "publish_auction",
  Cancelled: "cancel_auction",
};

const ViewAuction = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [auctionStatus, setAuctionStatus] = useState(null);
  const [bidId, setBidId] = useState(null); // stored after bid is created
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (auctionId) fetchAuctionDetail(auctionId);
  }, [auctionId]);

  const fetchAuctionDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load auction details.",
        );
        setIsLoading(false);
        return;
      }

      setAuctionStatus(res.statusDescription);

      // If the response includes a bidId (e.g. after bid creation), store it
      if (res.bidId) setBidId(res.bidId);

      form.setFieldsValue({
        auctionCode: res.auctionCode,
        instrumentType: res.instrumentType,
        currencyCode: res.currencyCode,
        issuingAuthority: res.issuingAuthority,
        statusDescription: res.statusDescription,
        auctionDate: parseDateArray(res.auctionDate),
        settlementDate: parseDateArray(res.settlementDate),
        maturityDate: res.maturityDate
          ? parseDateArray(res.maturityDate)
          : null,
        totalOffered: res.totalOffered ?? null,
        productId: res.productId ?? null,
        securityMasterId: res.securityMasterId ?? null,
      });

      setIsLoading(false);
    });
  };

  // ── Reusable helper — accepts method (default POST) and full url override ─
  const runApiAction = (
    urlSuffix,
    successMsg,
    method = "POST",
    urlOverride = null,
  ) => {
    setIsLoading(true);
    const body = { session, method };
    const url =
      urlOverride ??
      `${apiConfig.GetAuctions}/auctions/${auctionId}/${urlSuffix}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Action failed. Please try again.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, successMsg);
      setIsLoading(false);
      fetchAuctionDetail(auctionId); // refresh so status updates live
    });
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleAction = ({ key }) => {
    switch (key) {
      // POST actions
      case "open_auction":
        runApiAction("open", "Auction opened successfully.");
        break;
      case "close_auction":
        runApiAction("close", "Auction closed successfully.");
        break;
      case "publish_auction":
        runApiAction("publish-results", "Auction published successfully.");
        break;
      case "cancel_auction":
        runApiAction("cancel", "Auction cancelled successfully.");
        break;

      // PUT action — requires bidId
      case "submit_bid":
        if (!bidId) {
          BOM.AlertMsg(
            cnt.WARNING,
            "No bid found for this auction. Please create a bid first.",
          );
          return;
        }
        runApiAction(
          null,
          "Bid submitted successfully.",
          "PUT",
          `${apiConfig.GetAuctions}/auctions/${auctionId}/bids/${bidId}/submit`,
        );
        break;

      // Navigation actions
      case "add_auction_line":
        navigate(`/add-auction-line`, { state: auctionId });
        break;
      case "create_auction_bid":
        navigate(`/create-auction-bid`, { state: auctionId });
        break;
      case "record_auction_results":
        navigate(`/record-auction-result`, { state: auctionId });
        break;
      case "create_auction_allotment":
        navigate(`/create-auction-allotment`, { state: auctionId });
        break;
      default:
        break;
    }
  };

  // Only disable the one action that matches the current status
  const disabledKey = STATUS_TO_DISABLED_ACTION[auctionStatus] ?? null;

  const menuItem = (key, label, danger = false) => {
    const isDisabled = key === disabledKey;
    return {
      key,
      disabled: isDisabled,
      label: (
        <span
          style={{
            color: isDisabled ? "#D0D5DD" : danger ? "#B42318" : undefined,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        >
          {label}
        </span>
      ),
    };
  };

  const actionMenuItems = [
    menuItem("open_auction", "Open Auction"),
    menuItem("close_auction", "Close Auction"),
    menuItem("publish_auction", "Publish Auction Results"),
    { type: "divider" },
    menuItem("cancel_auction", "Cancel Auction", true),
    { type: "divider" },
    menuItem("add_auction_line", "Add Auction Line"),
    menuItem("create_auction_bid", "Create Auction Bid"),
    menuItem("submit_bid", "Submit Bid"),
    menuItem("record_auction_results", "Record Auction Results"),
    menuItem("create_auction_allotment", "Create Auction Allotment"),
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.viewAuction}>
      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Auction Details</h3>
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* Auction Code & Instrument Type */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Code"
                        tip="The unique code identifying this auction."
                      />
                    }
                    name="auctionCode"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Instrument Type"
                        tip="The type of instrument being auctioned."
                      />
                    }
                    name="instrumentType"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Currency & Issuing Authority */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Currency"
                        tip="The currency in which the auction is denominated."
                      />
                    }
                    name="currencyCode"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={CURRENCY_OPTIONS}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Issuing Authority"
                        tip="The authority responsible for issuing the instrument."
                      />
                    }
                    name="issuingAuthority"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Auction Date & Settlement Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Date"
                        tip="The date on which the auction was conducted."
                      />
                    }
                    name="auctionDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The date by which funds and securities are exchanged."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Status */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Status"
                        tip="The current status of this auction."
                      />
                    }
                    name="statusDescription"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(ViewAuction);
